import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { selectClient } from '@client/model/selectors';
import { useSession } from '@/features/guided-financial-planning/context';
import { useFundingSources } from './use-funding-sources';

/**
 * Hook to access coverage assessment data for the guided planning feature.
 *
 * For completed sessions, uses the snapshotted multipleFundingSources.
 * For active sessions, uses the live client data.
 *
 * This is the guided-planning-specific version of useCoverageAssessmentData.
 */
export function useCoverageAssessment() {
  const client = useSelector(selectClient);
  const { session } = useSession();
  const fundingSources = useFundingSources();

  const selectedAssets = session?.stepData?.selectedAssets ?? [];
  const userSelectedExistingLtcPolicy = selectedAssets.includes('existing-ltc-policy');

  // Round yearsTillLtc for display purposes, minimum 0
  const yearsTillLtc = useMemo(
    () => Math.max(0, Math.round(client.appliedInferenceSet.yearsTillLtc)),
    [client.appliedInferenceSet.yearsTillLtc],
  );

  // Coverage is measured against the full cost the client faces — professional
  // care PLUS out-of-pocket room & board (non-zero only for Independent Living).
  // Room & board is never policy-coverable, so IL coverage can never reach 100%
  // and the gap honestly includes it. (The policy still only pays for care.)
  const totalLtcCost = client.allPhaseCosts.allPhaseInflatedTotalCost;

  // Use snapshotted funding sources for completed sessions
  const multipleSelfFundingProjectedCostCoverage =
    fundingSources?.multipleSelfFundingProjectedCostCoverage ?? 0;
  const multiplePolicyProjectedCostCoverage =
    fundingSources?.multiplePolicyProjectedCostCoverage ?? 0;
  const combinedProjectedCostCoverage =
    session?.status === 'completed' && fundingSources
      ? (fundingSources.multipleSelfFundingProjectedCostCoverage ?? 0) +
        (fundingSources.multiplePolicyProjectedCostCoverage ?? 0)
      : (client.fundingSources?.combinedProjectedCostCoverage ?? 0);

  // When user didn't select Existing LTC on assets step
  const effectiveTotalCoverage = useMemo(
    () =>
      userSelectedExistingLtcPolicy
        ? combinedProjectedCostCoverage
        : multipleSelfFundingProjectedCostCoverage,
    [
      userSelectedExistingLtcPolicy,
      combinedProjectedCostCoverage,
      multipleSelfFundingProjectedCostCoverage,
    ],
  );

  const coverageGap = useMemo(
    () => Math.max(0, totalLtcCost - effectiveTotalCoverage),
    [totalLtcCost, effectiveTotalCoverage],
  );

  // Count of self-funding sources
  const selfFundingSourceCount = useMemo(
    () => Object.keys(fundingSources?.selfFunding ?? {}).length,
    [fundingSources?.selfFunding],
  );

  return {
    yearsTillLtc,
    totalLtcCost,
    multipleSelfFundingProjectedCostCoverage,
    multiplePolicyProjectedCostCoverage,
    combinedProjectedCostCoverage,
    effectiveTotalCoverage,
    coverageGap,
    selfFundingSourceCount,
    userSelectedExistingLtcPolicy,
  };
}
